import { useState, useRef } from "react";
import { Cpu, ArrowRight, Zap } from "lucide-react";
import { services } from "../data/serviceData";
import { formatRand } from "../lib/invoice";
import { useTheme } from "../context/ThemeContext";

function diagnose(input) {
  if (!input.trim()) return [];
  const lower = input.toLowerCase();
  const matches = [];
  for (const svc of services) {
    const score = svc.keywords.filter((kw) => lower.includes(kw)).length;
    if (score > 0) matches.push({ ...svc, score });
  }
  return matches.sort((a, b) => b.score - a.score).slice(0, 4);
}

const PLACEHOLDERS = [
  "My car makes a grinding noise when I brake…",
  "Engine warning light came on this morning…",
  "Car won't start, just a clicking sound…",
  "Steering wheel shakes at highway speed…",
];

export default function SmartDiagnosis({ onAddService }) {
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [analyzed, setAnalyzed] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [placeholder] = useState(
    () => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]
  );
  const textareaRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    setInput(val);
    if (val.length > 2) {
      setResults(diagnose(val));
      setAnalyzed(true);
    } else {
      setResults([]);
      setAnalyzed(false);
    }
  };

  return (
    <section
      id="diagnosis"
      className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20 relative overflow-hidden"
    >
      {/* BG image tint */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage:
            "url(/diagnosis-bg.webp)",
        }}
      />
      <div className={`absolute inset-0 bg-gradient-to-b ${isDark ? "from-[#050507] via-transparent to-[#050507]" : "from-white via-transparent to-white"}`} />

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6 ${isDark ? "bg-[#00F0FF]/10 border-[#00F0FF]/20" : "bg-brand-teal/10 border-brand-teal/20"}`}>
            <Cpu size={12} className={isDark ? "text-[#00F0FF]" : "text-brand-teal"} />
            <span className={`text-xs font-mono tracking-wider font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
              AI-POWERED
            </span>
          </div>
          <h2 className={`font-display font-bold text-4xl lg:text-5xl tracking-tighter mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
            Smart Diagnosis
          </h2>
          <p className={`text-base max-w-md mx-auto ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
            Describe what's wrong in plain English. Our system identifies the
            likely issue and recommends the right service.
          </p>
        </div>

        {/* Input area */}
        <div className={`relative rounded-2xl border transition-all duration-300 ${isDark ? "bg-[#0C0C0F] border-[#222226] focus-within:border-[#00F0FF]/50 focus-within:shadow-[0_0_32px_rgba(0,240,255,0.1)]" : "bg-white border-slate-200 shadow-[var(--shadow-card)] focus-within:border-brand-blue/40 focus-within:shadow-[0_0_0_3px_rgba(30,64,175,0.08)]"}`}>
          <div className="flex items-start gap-3 p-5">
            <Cpu size={18} className={`mt-1 flex-shrink-0 ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`} />
            <textarea
              ref={textareaRef}
              data-testid="diagnosis-input"
              value={input}
              onChange={handleChange}
              placeholder={placeholder}
              rows={3}
              className={`flex-1 bg-transparent placeholder-[#8F9098]/50 text-base resize-none outline-none leading-relaxed ${isDark ? "text-white" : "text-slate-900"}`}
            />
          </div>

          {/* Typing indicator */}
          {input.length > 0 && input.length <= 2 && (
            <div className="px-5 pb-4 flex items-center gap-2">
              <span className="text-xs text-[#8F9098]">Keep typing…</span>
            </div>
          )}

          {analyzed && results.length === 0 && input.length > 2 && (
            <div className="px-5 pb-4">
              <p className="text-xs text-[#8F9098]">
                No specific match found — try describing the sound, symptom, or warning light.
              </p>
            </div>
          )}
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-8 space-y-4">
            <p className={`text-xs uppercase tracking-[0.15em] font-mono ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
              Suggested services · {results.length} match{results.length > 1 ? "es" : ""}
            </p>
            {results.map((svc, i) => {
              const Icon = svc.Icon;
              const isTop = i === 0;
              return (
                <div
                  key={svc.id}
                  className={`relative rounded-2xl border p-5 transition-all duration-300 ${
                    isDark
                      ? isTop
                        ? "bg-gradient-to-br from-[#141419] to-[#0C0C0F] border-[#00F0FF]/25 shadow-[0_0_24px_-4px_rgba(0,240,255,0.12)]"
                        : "bg-[#141419] border-[#222226] hover:border-[#00F0FF]/20"
                      : isTop
                        ? "bg-white border-brand-blue/20 shadow-[var(--shadow-card-hover)]"
                        : "bg-white border-slate-200 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)]"
                  }`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  {/* TOP MATCH badge */}
                  {isTop && (
                    <div className={`absolute -top-2.5 left-4 px-3 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${
                      isDark
                        ? "bg-[#00F0FF] text-black shadow-[0_2px_12px_rgba(0,240,255,0.4)]"
                        : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_2px_12px_rgba(249,115,22,0.35)]"
                    }`}>
                      ⚡ TOP MATCH
                    </div>
                  )}

                  {/* Header row: icon + name + description */}
                  <div className="flex items-start gap-3.5 mb-4">
                    <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
                      isDark
                        ? isTop ? "bg-[#00F0FF]/15" : "bg-[#00F0FF]/8"
                        : isTop ? "bg-brand-teal/12" : "bg-brand-teal/8"
                    }`}>
                      <Icon size={20} className={isDark ? "text-[#00F0FF]" : "text-brand-teal"} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-display font-semibold text-base leading-tight mb-1 ${isDark ? "text-white" : "text-slate-900"}`}>
                        {svc.name}
                      </h4>
                      <p className={`text-sm leading-relaxed ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
                        {svc.short}
                      </p>
                    </div>
                  </div>

                  {/* Price + CTA row */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <span className={`text-[10px] uppercase tracking-wider ${isDark ? "text-[#8F9098]" : "text-slate-400"}`}>
                        Price range
                      </span>
                      <p className={`font-mono text-sm font-bold ${isDark ? "text-[#00F0FF]" : "text-brand-blue"}`}>
                        {formatRand(svc.priceMin)} – {formatRand(svc.priceMax)}
                      </p>
                    </div>
                    <button
                      data-testid={`diagnosis-add-${svc.id}`}
                      onClick={() => onAddService(svc.id)}
                      className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all duration-200 active:scale-95 min-h-[44px] ${
                        isDark
                          ? isTop
                            ? "bg-[#00F0FF] text-black shadow-[0_2px_16px_rgba(0,240,255,0.3)] hover:shadow-[0_4px_24px_rgba(0,240,255,0.45)]"
                            : "bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black"
                          : isTop
                            ? "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_2px_12px_rgba(249,115,22,0.3)]"
                            : "bg-brand-blue/8 text-brand-blue hover:bg-brand-blue hover:text-white"
                      }`}
                    >
                      Add Service
                      <ArrowRight size={14} />
                    </button>
                  </div>

                  {/* Duration chip */}
                  <div className={`inline-flex items-center gap-1 mt-3 px-2 py-0.5 rounded text-[10px] font-mono ${isDark ? "bg-white/5 text-[#8F9098]" : "bg-slate-100 text-slate-500"}`}>
                    <Zap size={10} /> {svc.duration} min
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Example hints */}
        {!input && (
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {["brakes squeaking", "won't start", "check engine light", "overheating"].map(
              (hint) => (
                <button
                  key={hint}
                  onClick={() => {
                    setInput(hint);
                    setResults(diagnose(hint));
                    setAnalyzed(true);
                  }}
                  className={`text-xs px-4 py-2 rounded-full border transition-all ${isDark ? "border-[#222226] text-[#8F9098] hover:border-[#00F0FF]/30 hover:text-white" : "border-slate-200 text-slate-500 hover:border-brand-blue/30 hover:text-brand-blue hover:bg-brand-blue/5"}`}
                >
                  {hint}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}
